import { useEffect, useState, useCallback } from 'react';
import type { SupplierDocument } from '../types/types';
import { supabase } from '../lib/supabase';
import { captureSupabaseError } from '../lib/sentry';

export function useSupplierDocuments(supplierId: string) {
  const [docs, setDocs] = useState<SupplierDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDocs = useCallback(async () => {
    if (!supplierId) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('supplier_documents') // 👈 assumes view exists
        .select('*')
        .eq('supplier_id', supplierId);

      if (error) throw error;

      const withUrls: SupplierDocument[] = [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const row of data as any[]) {
        let signedUrl: string | undefined;

        // 2. Get signed download URL
        const { data: urlData, error: urlError } = await supabase.storage
          .from('documents')
          .createSignedUrl(row.storage_path, 60 * 5);

        if (!urlError) {
          signedUrl = urlData?.signedUrl;
        }

        withUrls.push({
          documentId: row.document_id,
          supplierId: row.supplier_id,
          title: row.title,
          description: row.description,
          uploadedBy: row.uploaded_by,
          uploadedAt: row.uploaded_at,
          versionId: row.version_id,
          fileName: row.file_name,
          mimeType: row.mime_type,
          sizeBytes: row.size_bytes,
          storagePath: row.storage_path,
          versionUploadedAt: row.version_uploaded_at,
          signedUrl,
        });
      }

      setDocs(withUrls);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      captureSupabaseError(err, { op: 'supplier.documents.fetch' });
      console.error('Error fetching supplier documents:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [supplierId]);

  // Run initially when supplierId changes
  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  return { docs, loading, error, refetch: fetchDocs };
}
